import React, { useEffect, useRef, useState } from 'react';
import { CheckIcon, LogOut } from 'lucide-react';
import { useClerk, useUser } from '@clerk/clerk-react';
import { usePortalBrand } from '../../contexts/PortalBrandContext';
import { usePortalBrandContext } from '../../hooks/usePortalBrandContext';
import { brandInitials, SIDE_LABEL } from './v2Shell';

/**
 * The v2 sidebar's brand block (mock `.side .brand`: "IV / Brand / Ivonne").
 * It carries what the v1 chrome spread over two places: the brand switcher
 * (PortalBrandSwitcher, for staff with more than one brand) and the account
 * menu (UserProfile's sign-out). The mock draws no account control; sign-out
 * has to live somewhere, so it opens from here.
 */
const SidebarBrandBlock: React.FC = () => {
  const { brands, selectedBrand, setSelectedBrandId } = usePortalBrand();
  const { brandName } = usePortalBrandContext();
  const { user: clerkUser } = useUser();
  const { signOut } = useClerk();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const name = brandName ?? '';
  const canSwitch = brands.length > 1;

  return (
    <div ref={rootRef} className="relative mx-3 mb-1.5 mt-3.5 flex-none">
      <button
        type="button"
        data-testid="side-brand"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={canSwitch ? `Brand: ${name}. Switch brand or sign out` : `Brand: ${name}. Account menu`}
        onClick={() => setOpen((v) => !v)}
        className="flex h-11 w-full items-center gap-3 whitespace-nowrap rounded-[10px] bg-white/[.08] px-3 text-left text-[13px] text-white hover:bg-white/[.12] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
      >
        <b className="w-6 flex-none text-center text-[11px] font-semibold text-white/80">{brandInitials(name)}</b>
        <span className={`min-w-0 truncate ${SIDE_LABEL}`}>
          <small className="block text-[11px] leading-[1.2] text-white/55">Brand</small>
          {name}
        </span>
      </button>

      {open && (
        <div role="menu" className="absolute left-0 right-0 top-full z-50 mt-1.5 rounded-[10px] border border-line bg-white py-1 text-[13px] text-ink shadow-lg">
          {/* The brand list scrolls inside the menu (admins see every brand,
              40+); the email and Sign out rows below stay pinned. */}
          {canSwitch && (
            <div data-testid="side-brand-list" className="max-h-[min(60vh,28rem)] overflow-y-auto overscroll-contain">
              {brands.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setSelectedBrandId(b.id);
                    setOpen(false);
                  }}
                  className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-page"
                >
                  <span className="truncate">{b.displayName}</span>
                  {selectedBrand?.id === b.id && <CheckIcon aria-hidden="true" className="h-3.5 w-3.5 text-brand-navy" strokeWidth={1.75} />}
                </button>
              ))}
            </div>
          )}
          {canSwitch && <div className="my-1 border-t border-line" />}
          {clerkUser?.primaryEmailAddress?.emailAddress && (
            <div className="truncate px-3 py-1.5 text-[11px] text-ink-muted">{clerkUser.primaryEmailAddress.emailAddress}</div>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void signOut();
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-page"
          >
            <LogOut aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={1.75} />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};

export default SidebarBrandBlock;
